import { makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { deleteDataV2, fetchDataV2 } from "../../../api/utils";
import { ErrorMsg } from "../../../components/AppMessages";
import { TORCH_RED, TORCH_RED_BRIGHT } from "../../../constants";
import UserSearchField from "../common/Fields/UserSearchField";
import { downloadExcelFile } from "../utils";
import DeleteUserGroupDialog from "./subs/Dialogs/DeleteUserGroupDialog";
import UserGroupFormDialog from "./subs/Dialogs/UserGroupFormDialog";
import UserGroupsTable from "./subs/UserGroupsTable";

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
// Sized down from MUI's own "medium" Button default (0.875rem / 6px 16px) - matches the same
// reduction applied across every other Admin tile's New/Edit/Delete buttons (see
// adminActionButtonStyles.ts), this one just predates that shared file and still duplicates it
// locally.
const useStyles = makeStyles({
  button: {
    backgroundColor: TORCH_RED,
    color: "#FFF !important",
    borderRadius: "0px !important",
    border: "none !important",
    marginRight: "10px",
    fontSize: "0.75rem !important",
    padding: "4px 10px !important",
    "&:hover": {
      backgroundColor: `${TORCH_RED_BRIGHT} !important`,
      border: "none !important",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  buttonOutlined: {
    backgroundColor: "transparent !important",
    color: `${TORCH_RED} !important`,
    borderRadius: "0px !important",
    border: `1px solid ${TORCH_RED} !important`,
    marginRight: "10px",
    fontSize: "0.75rem !important",
    padding: "4px 10px !important",
    "&:hover": {
      backgroundColor: "rgba(244, 2, 42, 0.15) !important",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
    ".theme-dark &": {
      color: "#FFF !important",
    },
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const UserGroups = () => {
  const { brands, regions, markets } = useSelector((state: RootStateOrAny) => state.admin);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userGroupFormDialogOpen, setUserGroupDialogFormOpen] = useState<boolean>(false);
  const [selectedUserGroup, setSelectedUserGroup] = useState<any>(undefined);
  const [userGroups, setUserGroups] = useState<Array<any>>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const filteredUserGroups = userGroups.filter((userGroup) => userGroup.name.toLowerCase().includes(searchValue.toLowerCase()));

  const classes = useStyles();

  const handleExportUsers = () => {
    const columnHeaders = ["Name", "Brands", "Region", "Markets"];
    const data = [
      columnHeaders,
      ...userGroups.map((user) => [
        user.name,
        user.brands.map((brand: { brand: any }) => brand.brand).join(", "),
        user.regions.map((region: { region: any }) => region.region).join(", "),
        user.markets.map((market: { market: any }) => market.market).join(", "),
      ]),
    ];
    downloadExcelFile(data, "geoscope");
  };

  useEffect(() => {
    fetchDataV2("/user_service/get-all-groups").then((res) => {
      // handleError (api/utils.ts) returns undefined for a cancelled request or when the user
      // has already been logged out - neither case is an error to report here.
      if (!res) return;
      if (!res.error) {
        setUserGroups(res);
      } else {
        console.log(res.error);
      }
    });
  }, []);

  const handleToogleDeleteDialog = () => {
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleToggleUserGroupFormDialog = () => {
    setUserGroupDialogFormOpen(!userGroupFormDialogOpen);
    if (userGroupFormDialogOpen) {
      setSelectedUserGroup(undefined);
    }
  };

  const handleDeleteUserGroup = (id: number) => {
    deleteDataV2(`/user_service/delete-group?id=${id}`).then((res) => {
      if (!res.error) {
        setUserGroups((prevState) => prevState.filter((user) => user.id != id));
        setSelectedUserGroup(undefined);
        handleToogleDeleteDialog();
      } else {
        console.log(res.error);
      }
    });
  };

  return (
    <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
      <Box component="div" className="admin_tile">
        <Box className={classes.buttonsContainer} component="div" mb={3}>
          <Box component="div">
            <Button
              className={classes.button}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={() => {
                setSelectedUserGroup(undefined);
                handleToggleUserGroupFormDialog();
              }}
              data-test-id="btn-add-new-user-group"
            >
              New
            </Button>
            <Button
              className={classes.buttonOutlined}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={handleToggleUserGroupFormDialog}
              disabled={!selectedUserGroup}
              data-test-id="btn-edit-user-group"
            >
              Edit
            </Button>
            <Button
              className={classes.buttonOutlined}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={handleToogleDeleteDialog}
              disabled={!selectedUserGroup}
              data-test-id="btn-delete-user-group"
            >
              Delete
            </Button>
            <Button
              className={classes.buttonOutlined}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={handleExportUsers}
              data-test-id="btn-export-users"
            >
              Export to Excel
            </Button>
          </Box>
          <Box component="div">
            <UserSearchField value={searchValue} setValue={setSearchValue} />
          </Box>
        </Box>

        <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
          <UserGroupsTable
            selectedUserGroup={selectedUserGroup}
            setSelectedUserGroup={setSelectedUserGroup}
            setDeleteDialogOpen={setDeleteDialogOpen}
            userGroups={filteredUserGroups}
            brands={brands}
            regions={regions}
            markets={markets}
            searchValue={searchValue}
          />
        </Sentry.ErrorBoundary>

        {deleteDialogOpen && selectedUserGroup && (
          <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
            <DeleteUserGroupDialog
              open={deleteDialogOpen}
              handleClose={handleToogleDeleteDialog}
              handleDelete={handleDeleteUserGroup}
              userGroup={selectedUserGroup}
            />
          </Sentry.ErrorBoundary>
        )}

        {userGroupFormDialogOpen && (
          <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
            <UserGroupFormDialog
              open={userGroupFormDialogOpen}
              toggleOpen={handleToggleUserGroupFormDialog}
              setUserGroups={setUserGroups}
              selectedUserGroup={selectedUserGroup}
              brands={brands}
              regions={regions}
              markets={markets}
            />
          </Sentry.ErrorBoundary>
        )}
      </Box>
    </Sentry.ErrorBoundary>
  );
};

export default UserGroups;
